import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Loader2 } from 'lucide-react';
import PlaygroundDetailContent from '@/components/PlaygroundDetailContent';
import { useAuth } from '@/lib/AuthContext';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default function CourtDetail() {
  const { courtId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getLocation } = useOptimizedGeolocation();
  const queryClient = useQueryClient();

  const [userLocation, setUserLocation] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [mapboxToken, setMapboxToken] = useState(null);

  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const routeSourceRef = useRef(null);

  // Fetch playground data
  const { data: playground, isLoading, isError } = useQuery({
    queryKey: ['playground', courtId],
    queryFn: async () => {
      const results = await base44.entities.Playground.filter({ id: courtId });
      return results?.[0] || null;
    },
    enabled: !!courtId
  });

  // Set mapbox token from env
  useEffect(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (token) {
      setMapboxToken(token);
      mapboxgl.accessToken = token;
    }
  }, []);

  // Get user location
  useEffect(() => {
    getLocation({ useCache: true, timeout: 3000 })
      .then(location => {
        setUserLocation([location.lng, location.lat]);
      })
      .catch(err => {
        console.warn('Location failed:', err);
        setUserLocation(null);
      });
  }, [getLocation]);

  // Initialize mini map
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || !playground?.coords_json) return;

    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [playground.coords_json.lng, playground.coords_json.lat],
      zoom: 15,
      interactive: false
    });

    // Add court marker
    const el = document.createElement('div');
    el.style.width = '24px';
    el.style.height = '24px';
    el.style.backgroundColor = '#6E2BFF';
    el.style.borderRadius = '50%';
    el.style.boxShadow = '0 0 15px rgba(110, 43, 255, 0.8)';

    new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([playground.coords_json.lng, playground.coords_json.lat])
      .addTo(newMap);

    // Add user marker if location available
    if (userLocation) {
      const userEl = document.createElement('div');
      userEl.style.width = '12px';
      userEl.style.height = '12px';
      userEl.style.backgroundColor = '#ef4444';
      userEl.style.borderRadius = '50%';
      userEl.style.boxShadow = '0 0 10px rgba(239, 68, 68, 0.8)';

      new mapboxgl.Marker({ element: userEl, anchor: 'center' })
        .setLngLat(userLocation)
        .addTo(newMap);
    }

    mapInstance.current = newMap;

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [mapboxToken, playground?.coords_json, userLocation]);

  // Real-time subscription
  useEffect(() => {
    if (!user || !courtId) return;

    const unsubscribe = base44.entities.Playground.subscribe((event) => {
      if (event.data?.id === courtId) {
        queryClient.invalidateQueries({ queryKey: ['playground', courtId] });
      }
    });

    return () => unsubscribe();
  }, [user, courtId, queryClient]);

  // Route functions
  const fetchRoute = useCallback(async () => {
    if (!userLocation || !playground?.coords_json || !mapboxToken) return;

    setRouteLoading(true);
    try {
      const coords = `${userLocation[0]},${userLocation[1]};${playground.coords_json.lng},${playground.coords_json.lat}`;
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/walking/${coords}?access_token=${mapboxToken}&geometries=geojson&steps=true&language=fr`
      );
      const data = await response.json();

      if (data.routes && data.routes[0] && mapInstance.current) {
        setRouteData(data.routes[0]);

        if (!routeSourceRef.current) {
          mapInstance.current.addSource('route', {
            type: 'geojson',
            data: { type: 'Feature', geometry: data.routes[0].geometry }
          });
          mapInstance.current.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#3EDC81',
              'line-width': 4,
              'line-opacity': 0.85
            }
          });
          routeSourceRef.current = true;

          // Fit bounds to show route
          const bounds = new mapboxgl.LngLatBounds();
          bounds.extend(userLocation);
          bounds.extend([playground.coords_json.lng, playground.coords_json.lat]);
          mapInstance.current.fitBounds(bounds, { padding: 50 });
        } else {
          mapInstance.current.getSource('route').setData({
            type: 'Feature',
            geometry: data.routes[0].geometry
          });
        }
      }
    } catch (err) {
      console.error('Route fetch failed:', err);
    } finally {
      setRouteLoading(false);
    }
  }, [userLocation, playground?.coords_json, mapboxToken]);

  const clearRoute = useCallback(() => {
    if (mapInstance.current && routeSourceRef.current) {
      if (mapInstance.current.getLayer('route')) mapInstance.current.removeLayer('route');
      if (mapInstance.current.getSource('route')) mapInstance.current.removeSource('route');
      routeSourceRef.current = null;
    }
    setRouteData(null);

    // Reset map view
    if (mapInstance.current && playground?.coords_json) {
      mapInstance.current.flyTo({
        center: [playground.coords_json.lng, playground.coords_json.lat],
        zoom: 15
      });
    }
  }, [playground?.coords_json]);

  const handleRouteToggle = () => {
    if (routeData) {
      clearRoute();
    } else {
      fetchRoute();
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A0B10] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#6E2BFF] animate-spin" />
      </div>
    );
  }

  if (isError || !playground) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A0B10] flex flex-col items-center justify-center p-6">
        <MapPin className="w-12 h-12 text-gray-500 mb-4" />
        <p className="text-white text-lg mb-2">Terrain introuvable</p>
        <p className="text-gray-400 text-sm mb-6">Ce terrain n'existe pas ou a été supprimé.</p>
        <Button onClick={() => navigate('/map')} className="bg-[#6E2BFF] hover:bg-[#8B5CFF]">
          Retour à la carte
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0B10] overflow-auto">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 bg-[#0A0B10]/95 backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:text-[#6E2BFF] p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-lg truncate">{playground.name}</h1>
            {playground.city && (
              <div className="flex items-center gap-1.5 text-[#B3B6C2] text-xs">
                <MapPin className="w-3 h-3 text-[#6E2BFF] flex-shrink-0" />
                <span className="truncate">
                  {playground.address && `${playground.address}, `}
                  {playground.city}
                  {playground.zip && ` ${playground.zip}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mini Map */}
      {playground.coords_json && (
        <div
          ref={mapContainer}
          className="w-full h-40 bg-[#13151D]"
        />
      )}

      {/* Content */}
      <div className="p-4">
        <PlaygroundDetailContent
          playground={playground}
          showRoute={routeData !== null}
          setShowRoute={setRouteData}
          routeLoading={routeLoading}
          userLocation={userLocation}
          onRouteToggle={handleRouteToggle}
        />
      </div>
    </div>
  );
}
